// ─── Ligne « glisser pour agir » (Core) ───────────────────────────
// STANDARD app-wide de la suite (levé de MoPCal) :
//   glisser vers la GAUCHE  → 🗑️ supprimer
//   glisser vers la DROITE  → ✏️ éditer (et/ou 📄 dupliquer)
// Jamais de ✕ par élément dans la liste. Natif uniquement : sur le web le
// geste n'est pas fiable → l'appui long prend le relais côté écran.
import React, { useRef, useMemo } from 'react';
import { Platform, Text, View, StyleSheet } from 'react-native';
import { Swipeable, RectButton } from 'react-native-gesture-handler';
import { useUI } from '../theme/PreferencesUI';

// L'ESPACEMENT entre lignes est porté par CE composant (wrapper), PAS par la
// carte : sinon les boutons d'action, qui épousent la hauteur de la carte,
// dépassent de la marge. La carte enfant doit donc avoir marginBottom: 0.
export default function LigneSwipe({
  children, onSupprimer, onSupprimerSerie, onDupliquer, onEditer, onDeplacer,
  // Personnalisation de l'action « déplacer » (17/08, Cédric) : certains
  // écrans réutilisent ce même emplacement pour une action DIFFÉRENTE (ex.
  // valider un rattachement) — l'icône et la couleur doivent alors le dire,
  // ⇄/teal par défaut pour ne rien changer aux 30+ appelants existants.
  iconeDeplacer = '⇄', labelDeplacer = 'Déplacer', couleurDeplacer,
  enabled = true, espacement = 8,
}) {
  const UI = useUI();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const ref = useRef(null);

  if (Platform.OS === 'web' || !enabled) {
    return <View style={{ marginBottom: espacement }}>{children}</View>;
  }

  const droite = [];
  if (onSupprimer)      droite.push({ cle: 'sup',   ic: '🗑️', label: 'Supprimer', on: onSupprimer, style: s.action });
  if (onSupprimerSerie) droite.push({ cle: 'serie', label: 'Supprimer la série', on: onSupprimerSerie, style: s.actionSerie, serie: true });
  const renderRight = droite.length ? () => (
    <View style={{ flexDirection: 'row' }}>
      {droite.map(a => (
        <RectButton key={a.cle} style={a.style} accessibilityLabel={a.label} onPress={() => { ref.current?.close?.(); a.on?.(); }}>
          <View style={s.actionInner}>
            {a.serie ? (
              <View style={s.serieWrap}>
                <Text style={s.serieArriere}>🗑️</Text>
                <Text style={s.serieAvant}>🗑️</Text>
              </View>
            ) : (
              <Text style={s.ic}>{a.ic}</Text>
            )}
          </View>
        </RectButton>
      ))}
    </View>
  ) : undefined;

  const gauche = [];
  if (onEditer)    gauche.push({ cle: 'edit', ic: '✏️', label: 'Éditer',    on: onEditer,    style: s.actionEdit });
  if (onDeplacer)  gauche.push({ cle: 'depl', ic: iconeDeplacer, label: labelDeplacer, on: onDeplacer,
    style: couleurDeplacer ? [s.actionDepl, { backgroundColor: couleurDeplacer }] : s.actionDepl });
  if (onDupliquer) gauche.push({ cle: 'dup',  ic: '📄', label: 'Dupliquer', on: onDupliquer, style: s.actionDup });
  const renderLeft = gauche.length ? () => (
    <View style={{ flexDirection: 'row' }}>
      {gauche.map(a => (
        <RectButton key={a.cle} style={a.style} accessibilityLabel={a.label} onPress={() => { ref.current?.close?.(); a.on?.(); }}>
          <View style={s.actionInner}>
            <Text style={s.ic}>{a.ic}</Text>
          </View>
        </RectButton>
      ))}
    </View>
  ) : undefined;

  return (
    // overflow: hidden en filet de sécurité — le conteneur interne du
    // Swipeable clippe déjà, mais si sa mesure prend un frame de retard
    // (montage d'un lot de lignes d'un coup, ex. dépli d'accordéon), ce
    // calque-ci évite qu'un éclat du bouton d'action reste visible au repos.
    <View style={{ marginBottom: espacement, overflow: 'hidden', borderRadius: 14 }}>
      <Swipeable
        ref={ref}
        renderRightActions={renderRight}
        renderLeftActions={renderLeft}
        rightThreshold={40} leftThreshold={40}
        overshootRight={false} overshootLeft={false} friction={1.6}
      >
        {children}
      </Swipeable>
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  // Pas de marginBottom : le bouton épouse la hauteur de la ligne (l'écart entre
  // lignes vient de la marge propre de chaque carte).
  action:      { backgroundColor: UI.danger, justifyContent: 'center', borderRadius: 13 },
  actionSerie: { backgroundColor: '#8E2723', justifyContent: 'center', borderRadius: 13 },
  actionEdit:  { backgroundColor: UI.accent, justifyContent: 'center', borderRadius: 13 },
  actionDup:   { backgroundColor: UI.muted,  justifyContent: 'center', borderRadius: 13 },
  actionDepl:  { backgroundColor: '#2A9D8F', justifyContent: 'center', borderRadius: 13 },
  actionInner: { alignItems: 'center', justifyContent: 'center', width: 60 },
  ic:          { fontSize: 20, color: '#fff', fontWeight: '800' },
  serieWrap:   { width: 30, height: 28 },
  serieArriere:{ position: 'absolute', top: -1, right: -1, fontSize: 15, opacity: 0.75 },
  serieAvant:  { position: 'absolute', bottom: -1, left: -1, fontSize: 19 },
  txt:         { color: '#fff', fontSize: 12, fontWeight: '700' },
});
